// CommandPalette — single input that's both a slash-command palette
// and a project search. Typing `/` shows commands; anything else is
// fuzzy-searched against project schema. Replaces SearchBar on
// canvas/workspace pages.
//
// Dropdown item shape:
//   { kind: 'cmd', name, description, args, snippet }
//   { kind: 'item', type, key, score }
//
// Global shortcut: Cmd/Ctrl+K focuses the input.

import { commandDefs, commands } from './CommandBar.js'

const MAX_DROPDOWN = 20

function filterCommands(query) {
  const q = query.trim().toLowerCase()
  const tag = (c) => ({ kind: 'cmd', name: c.name, description: c.description, args: c.args })
  if (!q) return commandDefs.slice(0, MAX_DROPDOWN).map(tag)
  return commandDefs
    .filter((c) => c.name.toLowerCase().includes(q) || c.description?.toLowerCase().includes(q))
    .slice(0, MAX_DROPDOWN)
    .map(tag)
}

function fuzzySearchItems(el, query) {
  const q = query.trim().toLowerCase()
  if (!q) return []
  const data = el.getRootState?.()?.data || el.state?.root?.data
  if (!data) return []
  const out = []
  const scan = (obj, type) => {
    if (!obj) return
    for (const key of Object.keys(obj)) {
      if (key.toLowerCase().includes(q)) out.push({ kind: 'item', type, key })
      if (out.length >= MAX_DROPDOWN) return
    }
  }
  scan(data.pages, 'pages')
  scan(data.components, 'components')
  scan(data.functions, 'functions')
  return out.slice(0, MAX_DROPDOWN)
}

function runCommand(el, input) {
  const value = input.trim()
  if (!value.startsWith('/')) return null
  const [name, ...args] = value.slice(1).split(/\s+/)
  const handler = commands[name.toLowerCase()]
  if (!handler) return `Unknown: /${name}`
  try {
    return handler(el, args) || `Ran /${name}`
  } catch (e) {
    return `Error: ${e.message || e}`
  }
}

function highlightIndex(palette, delta) {
  const items = palette.Dropdown?.node?.querySelectorAll('[data-cp-item]')
  if (!items?.length) return
  const cur = Array.from(items).findIndex((i) => i.hasAttribute('data-cp-active'))
  items.forEach((i) => i.removeAttribute('data-cp-active'))
  const next = ((cur < 0 ? -1 : cur) + delta + items.length) % items.length
  const target = items[Math.max(0, next)]
  target?.setAttribute('data-cp-active', '1')
  target?.style?.setProperty?.('background', 'rgba(64,156,255,0.22)')
  Array.from(items).forEach((i, idx) => {
    if (idx !== next) i.style?.setProperty?.('background', '')
  })
}

function executeHighlighted(palette, el) {
  const active = palette.Dropdown?.node?.querySelector('[data-cp-item][data-cp-active]') ||
                 palette.Dropdown?.node?.querySelector('[data-cp-item]')
  if (!active) return null
  const kind = active.getAttribute('data-cp-kind')
  const name = active.getAttribute('data-cp-name')
  if (kind === 'cmd') {
    palette.Input.node.value = '/' + name + ' '
    palette.Input.node.focus()
    updateDropdown(palette, el)
    return null
  }
  if (kind === 'item') {
    const key = active.getAttribute('data-cp-key')
    const type = active.getAttribute('data-cp-type')
    el.call('openArtboardItem', type, key)
    palette.Input.node.value = ''
    palette.Input.node.blur()
    hideDropdown(palette)
    return null
  }
  return null
}

function hideDropdown(palette) {
  palette.Dropdown?.update?.({ style: { display: 'none' } })
}

function showDropdown(palette) {
  palette.Dropdown?.update?.({ style: { display: 'block' } })
}

function renderDropdown(palette, rows) {
  const node = palette.Dropdown?.node
  if (!node) return
  node.innerHTML = ''
  if (!rows.length) {
    hideDropdown(palette)
    return
  }
  showDropdown(palette)
  for (const row of rows) {
    const div = node.ownerDocument.createElement('div')
    div.setAttribute('data-cp-item', '1')
    div.setAttribute('data-cp-kind', row.kind)
    div.style.padding = '8px 12px'
    div.style.cursor = 'pointer'
    div.style.display = 'flex'
    div.style.justifyContent = 'space-between'
    div.style.gap = '12px'
    div.style.borderRadius = '4px'
    div.onmouseenter = () => { div.style.background = 'rgba(255,255,255,0.08)' }
    div.onmouseleave = () => { div.style.background = '' }
    if (row.kind === 'cmd') {
      div.setAttribute('data-cp-name', row.name)
      div.innerHTML =
        `<span><span style="font-family:monospace;color:#8ab4f8">/${row.name}</span>` +
        (Array.isArray(row.args) ? ` <span style="opacity:.55;font-size:11px">[${row.args.join('|')}]</span>` : '') +
        `</span><span style="opacity:.65;font-size:12px">${row.description || ''}</span>`
    } else {
      div.setAttribute('data-cp-name', row.key)
      div.setAttribute('data-cp-kind', 'item')
      div.setAttribute('data-cp-type', row.type)
      div.setAttribute('data-cp-key', row.key)
      div.innerHTML =
        `<span>${row.key}</span><span style="opacity:.55;font-size:11px">${row.type}</span>`
    }
    node.appendChild(div)
  }
}

function updateDropdown(palette, el) {
  const value = palette.Input?.node?.value || ''
  if (value.startsWith('/')) {
    const rows = filterCommands(value.slice(1))
    renderDropdown(palette, rows)
    return
  }
  if (value.trim()) {
    const rows = fuzzySearchItems(el, value)
    renderDropdown(palette, rows)
    return
  }
  // Empty + focused: show top commands.
  renderDropdown(palette, filterCommands(''))
}

function setFeedback(palette, msg) {
  const fb = palette.Feedback
  if (!fb?.update) return
  fb.update({ text: msg, style: { display: msg ? 'block' : 'none' } })
  if (msg) {
    clearTimeout(palette.__feedbackTimer)
    palette.__feedbackTimer = setTimeout(() => {
      fb.update({ style: { display: 'none' } })
    }, 2500)
  }
}

export const CommandPalette = {
  tag: 'div',
  position: 'relative',
  width: '100%',
  maxWidth: '560px',
  fontSize: 'Y',

  onInit: (el) => {
    // Cmd/Ctrl+K to focus from anywhere.
    if (el.__cpBound) return
    el.__cpBound = true
    const handler = (ev) => {
      if ((ev.metaKey || ev.ctrlKey) && ev.key.toLowerCase() === 'k') {
        ev.preventDefault()
        el.Input?.node?.focus()
        el.Input?.node?.select?.()
      }
    }
    document.addEventListener('keydown', handler)
    el.__cpUnbind = () => document.removeEventListener('keydown', handler)
  },

  Input: {
    tag: 'input',
    type: 'text',
    placeholder: 'Type / for command, or search',
    width: '100%',
    padding: 'W B',
    round: 'W',
    border: '1px solid rgba(255,255,255,0.12)',
    background: 'rgba(0,0,0,0.25)',
    color: 'white',
    fontSize: 'Y',
    style: { outline: 'none' },
    attr: { 'aria-label': 'Canvas command or search' },
    onFocus: (ev, el) => updateDropdown(el.parent, el.parent),
    onInput: (ev, el) => updateDropdown(el.parent, el.parent),
    onBlur: (ev, el) => {
      // Delay so click on dropdown item can fire first.
      setTimeout(() => hideDropdown(el.parent), 150)
    },
    onKeydown: (ev, el) => {
      const palette = el.parent
      if (ev.key === 'Escape') {
        el.node.value = ''
        el.node.blur()
        hideDropdown(palette)
        return
      }
      if (ev.key === 'ArrowDown') { ev.preventDefault(); highlightIndex(palette, +1); return }
      if (ev.key === 'ArrowUp')   { ev.preventDefault(); highlightIndex(palette, -1); return }
      if (ev.key === 'Enter') {
        ev.preventDefault()
        // Execute highlighted item, or command from input value.
        const result = executeHighlighted(palette, palette)
        if (result === null && palette.Input.node.value.startsWith('/')) {
          const msg = runCommand(palette, palette.Input.node.value)
          if (msg) setFeedback(palette, msg)
          palette.Input.node.value = ''
          hideDropdown(palette)
        }
      }
    }
  },

  Dropdown: {
    tag: 'div',
    position: 'absolute',
    top: 'calc(100% + 6px)',
    left: '0',
    right: '0',
    maxHeight: '60vh',
    overflow: 'auto',
    background: '#1c1f24',
    color: 'white',
    round: 'W',
    padding: 'W',
    fontSize: 'Y',
    style: {
      display: 'none',
      border: '1px solid rgba(255,255,255,0.1)',
      boxShadow: '0 10px 30px rgba(0,0,0,0.45)',
      zIndex: '100'
    },
    onPointerDown: (ev) => ev.preventDefault(), // keep input focused while clicking
    onClick: (ev, el) => {
      const item = ev.target.closest?.('[data-cp-item]')
      if (!item) return
      const kind = item.getAttribute('data-cp-kind')
      if (kind === 'cmd') {
        const name = item.getAttribute('data-cp-name')
        el.parent.Input.node.value = '/' + name + ' '
        el.parent.Input.node.focus()
        updateDropdown(el.parent, el.parent)
      } else if (kind === 'item') {
        const key = item.getAttribute('data-cp-key')
        const type = item.getAttribute('data-cp-type')
        el.parent.call('openArtboardItem', type, key)
        el.parent.Input.node.value = ''
        hideDropdown(el.parent)
      }
    }
  },

  Feedback: {
    tag: 'div',
    position: 'absolute',
    top: 'calc(100% + 6px)',
    left: '0',
    padding: 'W A',
    background: 'rgba(64,156,255,0.9)',
    color: 'white',
    round: 'W',
    fontSize: 'Y',
    style: { display: 'none', zIndex: '99' }
  }
}
